// StarDecision Mystical Theme - Celestial Color Palette

export const colors = {
  // Backgrounds - Deep cosmic colors
  background: {
    midnight: '#0a0a1a',
    cosmos: '#0f0f2d',
    nebula: '#1a1a3e',
    card: 'rgba(26, 26, 62, 0.95)',
    cardLight: 'rgba(45, 45, 90, 0.9)',
  },

  // Primary - Celestial violet spectrum
  primary: {
    celestial: '#4a3f8a',
    violet: '#6b5bbd',
    lavender: '#9d8fdb',
    light: '#b8aef0',
  },

  // Accents - Gold & Silver for premium feel
  accent: {
    gold: '#d4af37',
    goldLight: '#f4d03f',
    goldDark: '#b8960c',
    silver: '#c0c0d0',
    silverLight: '#e0e0f0',
  },

  // Text colors
  text: {
    starWhite: '#fafafe',
    primary: '#e8e8f8',
    secondary: '#a8a8c8',
    muted: '#6b6b8a',
  },

  // Status colors
  status: {
    success: '#4ade80',
    successBg: 'rgba(74, 222, 128, 0.15)',
    warning: '#fbbf24',
    warningBg: 'rgba(251, 191, 36, 0.15)',
    error: '#f87171',
    errorBg: 'rgba(248, 113, 113, 0.15)',
    info: '#60a5fa',
    infoBg: 'rgba(96, 165, 250, 0.15)',
  },

  // Border colors
  border: {
    subtle: 'rgba(157, 143, 219, 0.2)',
    medium: 'rgba(157, 143, 219, 0.35)',
    strong: 'rgba(157, 143, 219, 0.5)',
    gold: 'rgba(212, 175, 55, 0.5)',
  },
};

export const gradients = {
  // Background gradients
  cosmicBackground: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a3e 50%, #0f0f2d 100%)',
  cosmicRadial: 'radial-gradient(ellipse at top, #1a1a3e 0%, #0a0a1a 70%)',

  // Card gradients
  mysticalCard: 'linear-gradient(145deg, rgba(45, 45, 90, 0.9) 0%, rgba(26, 26, 62, 0.95) 100%)',
  glassCard: 'linear-gradient(145deg, rgba(74, 63, 138, 0.2) 0%, rgba(26, 26, 62, 0.3) 100%)',
  lightCard: 'linear-gradient(145deg, rgba(250, 250, 254, 0.98) 0%, rgba(232, 232, 248, 0.95) 100%)',

  // Button gradients
  primaryButton: 'linear-gradient(135deg, #6b5bbd 0%, #4a3f8a 100%)',
  goldButton: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
  goldButtonHover: 'linear-gradient(135deg, #f4d03f 0%, #d4af37 100%)',

  // Text gradients
  goldText: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 50%, #d4af37 100%)',
  violetText: 'linear-gradient(135deg, #9d8fdb 0%, #6b5bbd 100%)',

  // Accent gradients
  premium: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 50%, #d4af37 100%)',
  score: {
    excellent: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
    good: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
    fair: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
    poor: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)',
  },
};

export const shadows = {
  card: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(157, 143, 219, 0.1)',
  cardHover: '0 12px 40px rgba(0, 0, 0, 0.5), 0 0 20px rgba(157, 143, 219, 0.15)',
  button: '0 4px 16px rgba(107, 91, 189, 0.4)',
  goldButton: '0 4px 20px rgba(212, 175, 55, 0.4)',
  goldGlow: '0 0 30px rgba(212, 175, 55, 0.3)',
  violetGlow: '0 0 30px rgba(107, 91, 189, 0.3)',
  text: '0 2px 10px rgba(0, 0, 0, 0.3)',
  inset: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
};

export const typography = {
  fontFamily: {
    heading: "'Cinzel', serif",
    body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  fontSize: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '30px',
    '4xl': '36px',
    '5xl': '48px',
    '6xl': '60px',
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  letterSpacing: {
    tight: '-0.025em',
    normal: '0',
    wide: '0.05em',
    wider: '0.1em',
    widest: '0.2em',
  },
};

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  '2xl': '48px',
  '3xl': '64px',
};

export const borderRadius = {
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
  full: '9999px',
};

// Helper function to get cosmic background style
export const getCosmicBackground = (): React.CSSProperties => ({
  minHeight: '100vh',
  background: gradients.cosmicBackground,
  position: 'relative' as const,
});

// Helper function to get star pattern overlay
export const getStarPattern = (): React.CSSProperties => ({
  position: 'absolute' as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: `
    radial-gradient(2px 2px at 20px 30px, rgba(255,255,255,0.3), transparent),
    radial-gradient(2px 2px at 40px 70px, rgba(255,255,255,0.2), transparent),
    radial-gradient(1px 1px at 90px 40px, rgba(255,255,255,0.4), transparent),
    radial-gradient(2px 2px at 130px 80px, rgba(255,255,255,0.2), transparent),
    radial-gradient(1px 1px at 160px 120px, rgba(255,255,255,0.3), transparent)
  `,
  backgroundSize: '200px 200px',
  pointerEvents: 'none' as const,
  opacity: 0.6,
});

// Helper function to get mystical card style
export const getCardStyle = (variant: 'default' | 'glass' | 'light' | 'gold' = 'default'): React.CSSProperties => {
  const baseStyle: React.CSSProperties = {
    borderRadius: borderRadius.xl,
    padding: spacing.xl,
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
  };

  switch (variant) {
    case 'glass':
      return {
        ...baseStyle,
        background: gradients.glassCard,
        border: `1px solid ${colors.border.subtle}`,
        boxShadow: shadows.card,
      };
    case 'light':
      return {
        ...baseStyle,
        background: gradients.lightCard,
        border: `1px solid rgba(157, 143, 219, 0.3)`,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      };
    case 'gold':
      return {
        ...baseStyle,
        background: gradients.mysticalCard,
        border: `2px solid ${colors.border.gold}`,
        boxShadow: `${shadows.card}, ${shadows.goldGlow}`,
      };
    default:
      return {
        ...baseStyle,
        background: gradients.mysticalCard,
        border: `1px solid ${colors.border.subtle}`,
        boxShadow: shadows.card,
      };
  }
};

// Helper function to get button style
export const getButtonStyle = (variant: 'primary' | 'gold' | 'outline' | 'ghost' = 'primary'): React.CSSProperties => {
  const baseStyle: React.CSSProperties = {
    padding: '14px 28px',
    borderRadius: borderRadius.md,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    fontFamily: typography.fontFamily.body,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none',
    border: 'none',
  };

  switch (variant) {
    case 'gold':
      return {
        ...baseStyle,
        background: gradients.goldButton,
        color: colors.background.midnight,
        boxShadow: shadows.goldButton,
      };
    case 'outline':
      return {
        ...baseStyle,
        background: 'transparent',
        color: colors.accent.gold,
        border: `2px solid ${colors.accent.gold}`,
        boxShadow: 'none',
      };
    case 'ghost':
      return {
        ...baseStyle,
        background: 'rgba(157, 143, 219, 0.1)',
        color: colors.text.starWhite,
        border: `1px solid ${colors.border.subtle}`,
        boxShadow: 'none',
      };
    default:
      return {
        ...baseStyle,
        background: gradients.primaryButton,
        color: colors.text.starWhite,
        boxShadow: shadows.button,
      };
  }
};

// Helper function to get input style
export const getInputStyle = (variant: 'dark' | 'light' = 'dark'): React.CSSProperties => {
  if (variant === 'light') {
    return {
      width: '100%',
      padding: '14px 18px',
      borderRadius: borderRadius.md,
      border: `1px solid rgba(107, 91, 189, 0.3)`,
      background: 'rgba(255, 255, 255, 0.9)',
      color: colors.background.midnight,
      fontSize: typography.fontSize.base,
      fontFamily: typography.fontFamily.body,
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
      outline: 'none',
    };
  }

  return {
    width: '100%',
    padding: '14px 18px',
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border.medium}`,
    background: 'rgba(10, 10, 26, 0.6)',
    color: colors.text.starWhite,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.body,
    transition: 'all 0.3s ease',
    boxSizing: 'border-box' as const,
    outline: 'none',
  };
};

// Helper function to get label style
export const getLabelStyle = (variant: 'dark' | 'light' = 'dark'): React.CSSProperties => ({
  display: 'block',
  fontSize: typography.fontSize.sm,
  fontWeight: typography.fontWeight.medium,
  color: variant === 'light' ? colors.primary.celestial : colors.text.secondary,
  marginBottom: spacing.sm,
  fontFamily: typography.fontFamily.body,
  letterSpacing: typography.letterSpacing.wide,
  textTransform: 'uppercase' as const,
});

// Helper function to get heading style
export const getHeadingStyle = (level: 1 | 2 | 3 | 4 = 1): React.CSSProperties => {
  const sizes = {
    1: typography.fontSize['5xl'],
    2: typography.fontSize['3xl'],
    3: typography.fontSize['2xl'],
    4: typography.fontSize.xl,
  };

  return {
    fontFamily: typography.fontFamily.heading,
    fontSize: sizes[level],
    fontWeight: typography.fontWeight.bold,
    color: colors.text.starWhite,
    letterSpacing: typography.letterSpacing.wide,
    margin: 0,
    textShadow: shadows.text,
  };
};

// Helper function to get gradient text style
export const getGradientTextStyle = (gradient: 'gold' | 'violet' = 'gold'): React.CSSProperties => ({
  background: gradient === 'gold' ? gradients.goldText : gradients.violetText,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
});

// Helper function for status badges
export const getStatusBadgeStyle = (status: 'premium' | 'trial' | 'free'): React.CSSProperties => {
  const statusColors = {
    premium: {
      bg: 'rgba(212, 175, 55, 0.2)',
      border: colors.accent.gold,
      text: colors.accent.goldLight,
    },
    trial: {
      bg: colors.status.warningBg,
      border: colors.status.warning,
      text: colors.status.warning,
    },
    free: {
      bg: 'rgba(157, 143, 219, 0.2)',
      border: colors.primary.lavender,
      text: colors.primary.lavender,
    },
  };

  const style = statusColors[status];

  return {
    padding: '6px 16px',
    borderRadius: borderRadius.full,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
    fontFamily: typography.fontFamily.body,
    letterSpacing: typography.letterSpacing.wider,
    textTransform: 'uppercase' as const,
    background: style.bg,
    border: `1px solid ${style.border}`,
    color: style.text,
  };
};

// Helper function for score indicator style
export const getScoreStyle = (score: number): { bg: string; text: string; label: string; gradient: string } => {
  if (score >= 85) {
    return {
      bg: 'rgba(212, 175, 55, 0.2)',
      text: colors.accent.goldLight,
      label: 'Excellent',
      gradient: gradients.score.excellent,
    };
  }
  if (score >= 70) {
    return {
      bg: colors.status.successBg,
      text: colors.status.success,
      label: 'Good',
      gradient: gradients.score.good,
    };
  }
  if (score >= 50) {
    return {
      bg: colors.status.warningBg,
      text: colors.status.warning,
      label: 'Fair',
      gradient: gradients.score.fair,
    };
  }
  return {
    bg: colors.status.errorBg,
    text: colors.status.error,
    label: 'Challenging',
    gradient: gradients.score.poor,
  };
};

const theme = {
  colors,
  gradients,
  shadows,
  typography,
  spacing,
  borderRadius,
  getCosmicBackground,
  getStarPattern,
  getCardStyle,
  getButtonStyle,
  getInputStyle,
  getLabelStyle,
  getHeadingStyle,
  getGradientTextStyle,
  getStatusBadgeStyle,
  getScoreStyle,
};

export default theme;
